"use client"

import { useState, useTransition, type FormEvent } from "react"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import { useT } from "@lib/i18n/use-t"

/**
 * Minimal search box for a seller storefront. Pushes `?q=<term>` and
 * resets pagination. Backend filters via `/store/sellers/:slug/products?q=`.
 */
export default function SellerSearch() {
  const t = useT()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [value, setValue] = useState(searchParams.get("q") ?? "")
  const [isPending, startTransition] = useTransition()

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const params = new URLSearchParams(searchParams.toString())
    params.delete("page")
    if (value.trim()) {
      params.set("q", value.trim())
    } else {
      params.delete("q")
    }
    const qs = params.toString()
    startTransition(() => {
      router.push(qs ? `${pathname}?${qs}` : pathname)
    })
  }

  return (
    <form onSubmit={submit} className="flex w-full max-w-md items-center gap-2">
      <input
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t("sellerSearchPlaceholder")}
        className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-light focus:outline-none dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:border-brand-dark"
      />
      <button
        type="submit"
        disabled={isPending}
        className="h-9 shrink-0 rounded-lg bg-brand-light px-4 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-50 dark:bg-brand-dark"
      >
        →
      </button>
    </form>
  )
}
